import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { type Language, t } from '../i18n';
import type { ChatMessage } from '../hooks/useChat';
import { ChatSuggestions, type ChatSuggestion } from './ChatSuggestions';
import { ChatProgress } from './ChatProgress';

interface ChatInterfaceProps {
  language: Language;
  messages: ChatMessage[];
  isLoading: boolean;
  isThinking: boolean;
  isComplete: boolean;
  error: string | null;
  currentSuggestions?: ChatSuggestion[];
  canForceComplete?: boolean;
  pendingAutoComplete?: boolean;
  userMessageCount?: number;
  onSendMessage: (message: string) => void;
  onGenerateReport: () => void;
  onForceComplete?: () => void;
  conclusionMessage?: string | null;
  assessmentType?: 'automation' | 'startup' | 'project';
}

export function ChatInterface({
  language,
  messages,
  isLoading,
  isThinking,
  isComplete,
  error,
  currentSuggestions = [],
  canForceComplete = false,
  pendingAutoComplete = false,
  userMessageCount = 0,
  onSendMessage,
  onGenerateReport,
  onForceComplete,
  assessmentType = 'automation',
}: ChatInterfaceProps) {
  // Generate conclusion message when assessment completes
  const conclusionText = isComplete
    ? (language === 'it'
      ? assessmentType === 'startup'
        ? 'Grazie per le informazioni. Ho un quadro chiaro della tua idea. Clicca il pulsante qui sotto per ricevere il report personalizzato con l\'analisi della tua startup.'
        : assessmentType === 'project'
          ? 'Grazie per le informazioni. Ho un quadro chiaro del progetto. Clicca il pulsante qui sotto per ricevere il report personalizzato con l\'analisi tecnica.'
          : 'Grazie per le informazioni. Ho un quadro chiaro della vostra situazione. Clicca il pulsante qui sotto per ricevere il report personalizzato con le opportunità di automazione.'
      : 'Thank you for the information. I have a clear picture of your situation. Click the button below to receive the personalized report.')
    : null;
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isLoading && !isComplete) {
      onSendMessage(input.trim());
      setInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSuggestionSelect = (suggestion: ChatSuggestion) => {
    // Use the response text if provided, otherwise use the button text
    const messageToSend = suggestion.response || suggestion.text;
    onSendMessage(messageToSend);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)] max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground">
          {t(language, 'chat.title')}
        </h2>
        {isComplete && (
          <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
            {t(language, 'chat.complete')}
          </span>
        )}
      </div>

      {/* Progress Indicator */}
      {!isComplete && messages.length > 0 && (
        <div className="pt-4">
          <ChatProgress
            userMessageCount={userMessageCount}
            maxQuestions={10}
            language={language}
          />
        </div>
      )}

      {/* Tips */}
      <div className="py-3 border-b border-border">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="font-medium">{t(language, 'chat.tips.title')}:</span>
          <span>{t(language, 'chat.tips.honest')}</span>
          <span className="text-border">|</span>
          <span>{t(language, 'chat.tips.specific')}</span>
        </div>
      </div>

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto py-4 space-y-4"
        role="log"
        aria-live="polite"
        aria-label={t(language, 'chat.title')}
      >
        {messages.map((message, index) => {
          const isStreaming = isLoading && index === messages.length - 1 && message.role === 'assistant';
          const isEmpty = message.content.length === 0;

          return (
            <div
              key={index}
              className={`chat-bubble flex ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground'
                }`}
                role="article"
                aria-label={message.role === 'user' ? t(language, 'chat.you') : t(language, 'chat.assessor')}
              >
                {message.role === 'assistant' ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    {isEmpty && isStreaming ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <div className="loading-dots" aria-hidden="true">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <span className="text-sm">{t(language, isThinking ? 'chat.thinking' : 'chat.typing')}</span>
                      </div>
                    ) : (
                      <>
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                        {isStreaming && (
                          <span className="inline-block w-2 h-4 bg-muted-foreground/50 animate-pulse ml-1" aria-hidden="true" />
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <p>{message.content}</p>
                )}
                {!isEmpty && (
                  <p
                    className={`text-xs mt-2 ${
                      message.role === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    }`}
                    aria-hidden="true"
                  >
                    {message.timestamp.toLocaleTimeString(language === 'it' ? 'it-IT' : 'en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {/* Loading indicator - show during initChat (no messages) or when last message is not assistant */}
        {isLoading && (messages.length === 0 || messages[messages.length - 1].role !== 'assistant') && (
          <div className="chat-bubble flex justify-start" aria-busy="true">
            <div className="bg-muted rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="loading-dots" aria-hidden="true">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="text-sm">{t(language, isThinking ? 'chat.thinking' : 'chat.typing')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="chat-bubble flex justify-center" role="alert">
            <div className="bg-destructive/10 text-destructive rounded-xl px-4 py-3 text-sm">
              {error}
            </div>
          </div>
        )}

        {/* Conclusion message when assessment completes */}
        {isComplete && conclusionText && (
          <div className="chat-bubble flex justify-start">
            <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800">
              <div className="prose prose-sm max-w-none">
                <p className="flex items-center gap-2">
                  <span className="text-green-600 text-lg">✓</span>
                  {conclusionText}
                </p>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestion buttons — hidden when we're about to auto-complete so the
          user isn't nudged to keep the conversation going past the threshold. */}
      {!isComplete && !isLoading && !pendingAutoComplete && currentSuggestions.length > 0 && (
        <div className="py-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">
            {language === 'it' ? 'Risposte rapide:' : 'Quick replies:'}
          </p>
          <ChatSuggestions
            suggestions={currentSuggestions}
            onSelect={handleSuggestionSelect}
            disabled={isLoading}
          />
        </div>
      )}

      {/* Force complete fallback button - shown when 10+ messages but no completion */}
      {canForceComplete && onForceComplete && (
        <div className="py-3 border-t border-border">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              {language === 'it'
                ? 'La conversazione sembra completa. Puoi procedere con il report.'
                : 'The conversation seems complete. You can proceed to the report.'}
            </p>
            <button
              onClick={onForceComplete}
              className="btn-secondary text-sm py-1.5 px-3 bg-yellow-100 dark:bg-yellow-800 hover:bg-yellow-200 dark:hover:bg-yellow-700 border-yellow-300 dark:border-yellow-600"
            >
              {language === 'it' ? 'Completa e genera report' : 'Complete and generate report'}
            </button>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="pt-4 border-t border-border">
        {isComplete ? (
          <button
            onClick={onGenerateReport}
            className="btn-primary w-full"
            aria-label={t(language, 'chat.generateReport')}
          >
            {t(language, 'chat.generateReport')}
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="flex gap-3" aria-label={t(language, 'chat.sendMessage')}>
            <label htmlFor="chat-input" className="sr-only">
              {t(language, 'chat.placeholder')}
            </label>
            <textarea
              id="chat-input"
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t(language, 'chat.placeholder')}
              disabled={isLoading}
              aria-disabled={isLoading}
              rows={2}
              className="form-input flex-1 resize-none"
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              aria-disabled={!input.trim() || isLoading}
              className="btn-primary px-6 self-end"
              aria-label={t(language, 'chat.send')}
            >
              {t(language, 'chat.send')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
